import { useEffect, useRef, useState } from "react";
import { useMap, FeatureGroup } from "react-leaflet";
import L from "leaflet";
import type { RealEstateSearchResult } from "../../types";
import { createRoot } from "react-dom/client";
import { formatArea } from "../../utils";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import { Box, FormControl, FormLabel, Grid, Typography } from "@mui/material";
import { Button, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import PlaceIcon from "@mui/icons-material/Place";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import { REAL_ESTATE_TYPE_MAP } from "../MapSearchDrawer";
import type { KaokuNumberSearchChiban } from "@/types/kaokuNumber";

interface RealEstateSearchLayerProps {
  defaultVisible: boolean;
  realEstateSearchData: RealEstateSearchResult[];
  handleKaokuNumberSearch?: (chibanList: KaokuNumberSearchChiban[]) => void;
  kaokuNumberSearchFlag: boolean;
}

export const REAL_ESTATE_SEARCH_LAYER_NAME = "不動産検索の結果を表示する";

export const RealEstateSearchLayer: React.FC<RealEstateSearchLayerProps> = ({
  defaultVisible,
  realEstateSearchData,
  handleKaokuNumberSearch,
  kaokuNumberSearchFlag,
}) => {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup>(L.layerGroup());
  const [visible, setVisible] = useState(defaultVisible);

  useEffect(() => {
    if (!map) return;

    const handleOverlayAdd = (e: L.LayersControlEvent): void => {
      if (e.name === REAL_ESTATE_SEARCH_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: L.LayersControlEvent): void => {
      if (e.name === REAL_ESTATE_SEARCH_LAYER_NAME) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !defaultVisible || !realEstateSearchData) return;

    if (layerRef.current) {
      layerRef.current.clearLayers();
    }

    if (realEstateSearchData.length) {
      const markers = realEstateSearchData.map((plot) =>
        createRealEstateSearchMarker(
          map,
          plot,
          kaokuNumberSearchFlag,
          handleKaokuNumberSearch
        )
      );
      markers.forEach((marker) => {
        layerRef.current?.addLayer(marker);
      });
    }

    if (visible) {
      layerRef.current.addTo(map);
    } else {
      map.removeLayer(layerRef.current);
    }
  }, [map, visible, realEstateSearchData]);

  return <FeatureGroup />;
};

/**
 * 不動産検索結果のポップアップを生成する関数
 * @param plot 不動産検索結果データ
 * @return L.Popup
 */
const createRealEstateSearchPopup = (
  map: L.Map,
  item: RealEstateSearchResult,
  kaokuNumberSearchFlag: boolean,
  handleKaokuNumberSearch?: (chibanList: KaokuNumberSearchChiban[]) => void
): L.Popup => {
  // popupの内容として表示するHTMLを生成する
  const popupHTML = document.createElement("div");

  const buttonState: boolean =
    item.prefName !== "" &&
    item.cityName !== "" &&
    /*
    大字、字については存在しない場合もあるので空文字でもボタンは有効にする
    ooazaName !== "" &&
    azaName !== "" &&
    */
    item.chibanKaokuNumber !== "";

  const handleClickKaokuNumberSearchButton = (): void => {
    if (handleKaokuNumberSearch) {
      const c: KaokuNumberSearchChiban = {
        prefectureName: item.prefName,
        location: item.location.replace(item.cityName, ""),
        chiban: item.chibanKaokuNumber,
      };
      handleKaokuNumberSearch([c]);
    }
  };

  const getRealEstateTypeForUrl = (realEstateType: string): string => {
    if (realEstateType === REAL_ESTATE_TYPE_MAP.building.label) {
      return "BUILDING";
    } else if (realEstateType === REAL_ESTATE_TYPE_MAP.condominium.label) {
      return "CONDOMINIUM";
    } else if (realEstateType === REAL_ESTATE_TYPE_MAP.land.label) {
      return "LAND";
    } else {
      return "ONE_BUILDING";
    }
  };

  // 地図を表示しているコンテナの横幅
  const mapContainerSizeX = map.getSize().x;

  // ポップアップのデフォルトサイズ(px)
  let popupWidth = 300;

  // 地図コンテナ幅375px以下では（300px幅のポップアップ＋余白）がきれいに収まらないので、ポップアップ幅を縮小する
  if (mapContainerSizeX < 375) {
    // PopupContent.tsxと同じに0.8倍にすると家屋番号検索ボタンが改行されてしまうため、0.85倍に変更
    popupWidth = Math.floor(mapContainerSizeX * 0.85);
  }

  createRoot(popupHTML).render(
    <Box sx={{ width: popupWidth }}>
      <FormControl>
        <FormLabel>
          <PlaceIcon fontSize={"inherit"} />
          住居表示
        </FormLabel>
        <Typography
          ml={2}
          my={1}
          fontSize={"0.8rem"}
          variant={"body1"}
          component={"span"}
        >
          {NYHalfWidthNumberToFillWidthNumber(`${item.address}`)}
        </Typography>
        <FormLabel>
          <ImportContactsIcon fontSize={"inherit"} /> 地番
        </FormLabel>
        <Typography
          ml={2}
          my={1}
          fontSize={"0.8rem"}
          variant={"body1"}
          component={"span"}
        >
          {NYHalfWidthNumberToFillWidthNumber(
            `${item.prefName}${item.location}${item.chibanKaokuNumber}`
          )}
        </Typography>
        {item.buildingName && (
          <>
            <FormLabel>
              <ImportContactsIcon fontSize={"inherit"} /> 建物名
            </FormLabel>
            <Typography
              ml={2}
              my={1}
              fontSize={"0.8rem"}
              variant={"body1"}
              component={"span"}
            >
              {item.buildingName}
            </Typography>
          </>
        )}
        <FormLabel>
          <SquareFootIcon fontSize={"inherit"} />
          推計地積(㎡)
        </FormLabel>
        <Typography
          ml={2}
          my={1}
          fontSize={"0.8rem"}
          variant={"body1"}
          component={"span"}
        >
          {formatArea(item.area)}
        </Typography>
      </FormControl>
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              label={"登記を取得"}
              disabled={!buttonState}
              onClick={() => {
                let url = "/acquirebook";
                if (item.prefName && item.location && item.chibanKaokuNumber) {
                  url =
                    url +
                    "?" +
                    `prefecture=${item.prefName}` +
                    `&city=${item.cityName}` +
                    `&location=${NYHalfWidthNumberToFillWidthNumber(
                      item.location.replace(item.cityName, "")
                    )}` +
                    `&chiban=${NYHalfWidthNumberToFillWidthNumber(
                      item.chibanKaokuNumber
                    )}` +
                    `&realEstate=${getRealEstateTypeForUrl(
                      item.realEstateType
                    )}`;
                }
                window.open(url, "_blank");
              }}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Contained}
            />
          </Grid>
          {kaokuNumberSearchFlag && (
            <Grid item xs={6}>
              <Button
                label={"家屋番号検索"}
                disabled={
                  !handleKaokuNumberSearch ||
                  [
                    REAL_ESTATE_TYPE_MAP.building.label,
                    REAL_ESTATE_TYPE_MAP.condominium.label,
                  ].includes(item.realEstateType)
                }
                onClick={handleClickKaokuNumberSearchButton}
                size={SizingWrapperStyle.INHERIT}
                variant={ButtonVariantOption.Contained}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
  return L.popup({
    content: popupHTML,
  });
};

/**
 * 不動産検索結果プロットのマーカーを生成し、クリックイベントを追加する関数
 * @param plot 不動産検索結果データ
 * @return L.Marker
 */
const createRealEstateSearchMarker = (
  map: L.Map,
  plot: RealEstateSearchResult,
  kaokuNumberSearchFlag: boolean,
  handleKaokuNumberSearch?: (chibanList: KaokuNumberSearchChiban[]) => void
): L.Marker => {
  const RealEstateSearchPopup = createRealEstateSearchPopup(
    map,
    plot,
    kaokuNumberSearchFlag,
    handleKaokuNumberSearch
  );

  const marker = L.marker([plot.latitude, plot.longitude]);

  marker.bindPopup(RealEstateSearchPopup);

  marker.on("click", () => {
    marker.openPopup();
  });
  return marker;
};
